import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BadgeCheck, Eye, Flame, GraduationCap, Loader2, Plus, Search, Wrench } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { PageIntro } from "@/components/layout/PageIntro";
import { Reveal } from "@/components/Reveal";
import api, { mediaUrl } from "@/lib/api";
import { GhostButton, StoreButton } from "@/components/Buttons";
import { useAuth } from "@/context/AuthContext";
import { SoldBy } from "@/components/SoldBy";
import { useMall } from "@/context/MallContext";
import { MALL_THEME, cardTheme, sortStoreFirst, tint } from "@/lib/mall";
import { PageBanner } from "@/components/mall/PageBanner";

export const KIND_TABS = [
    { key: "machine", label: "Machines" },
    { key: "supply", label: "Supplies" },
    { key: "blank", label: "Blanks" },
    { key: "apparel", label: "Apparel" },
    { key: "class", label: "Classes" },
    { key: "wanted", label: "Wanted" },
];

export const money = (n) => (n === null || n === undefined ? "Make an offer" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: Number(n) % 1 ? 2 : 0, maximumFractionDigits: 2 })}`);

/** The shop you are in shows its ads in its own colors. Every other seller stays black and white. */
export function ListingCard({ l, to }) {
    const { store: active } = useMall();
    const mine = Boolean(active?.slug && (l.store?.slug || l.store_slug) === active.slug);
    const t = cardTheme(l, active);
    return (
        <Link to={to || `/supplies/${l.slug}`} data-testid={`listing-card-${l.slug}`} data-mine={mine || undefined}
            style={mine ? { "--c": t.primary, borderColor: tint(t.primary, 0.5), background: tint(t.primary, 0.06) } : undefined}
            className={`group flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] transition-all duration-300 hover:-translate-y-1.5 ${mine ? "hover:border-[color:var(--c)]" : "hover:border-zinc-400 grayscale"}`}>
            <div className="relative aspect-[4/3] overflow-hidden bg-black/50">
                {l.images?.[0] ? (
                    <img src={mediaUrl(l.images[0])} alt={l.title} loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                ) : (
                    <div className="flex h-full items-center justify-center text-zinc-600"><Search size={28} /></div>
                )}
                {mine && (
                    <span className="absolute left-3 top-3 rounded-full px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-widest"
                        style={{ background: t.primary, color: t.on }} data-testid={`listing-mine-${l.slug}`}>
                        This shop
                    </span>
                )}
                {l.featured && !mine && (
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-zinc-900 px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-white">
                        <Flame size={11} /> Featured
                    </span>
                )}
                {l.status === "sold" && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/70 font-display text-2xl font-black uppercase tracking-widest text-red-400">Sold</span>
                )}
                <span className="absolute right-3 top-3 rounded-full border border-white/20 bg-black/70 px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest text-[#f4f4f5]">
                    {l.condition}
                </span>
            </div>
            <div className="flex flex-1 flex-col p-5">
                <p className="font-mono text-2xl font-black" style={{ color: mine ? (t.ink || t.primary) : "#18181b" }}>{money(l.price)}</p>
                <h3 className="mt-1 font-display text-base font-bold leading-tight">{l.title}</h3>
                <p className="mt-2 flex-1 line-clamp-2 text-sm text-zinc-400">{l.description}</p>
                <SoldBy store={l.store || { slug: l.store_slug || "", name: l.seller_name, city: l.location, verified: l.seller_kind === "shop" }} className="mt-3" withFulfillment={false} />
                <div className="mt-2 flex items-center justify-between font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                    <span>{l.store?.fulfillment_line || l.location}</span>
                    <span className="inline-flex items-center gap-1"><Eye size={11} /> {l.views || 0}</span>
                </div>
            </div>
        </Link>
    );
}

export default function Marketplace() {
    const [sp, setSp] = useSearchParams();
    const { user } = useAuth();
    const { store: active } = useMall();
    const kind = KIND_TABS.some((t) => t.key === sp.get("kind")) ? sp.get("kind") : sp.get("q") ? null : "machine";
    const [q, setQ] = useState(sp.get("q") || "");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get("/marketplace/listings", { params: { kind: kind || undefined, q: sp.get("q") || undefined } })
            .then(({ data }) => setData(data)).catch(() => { }).finally(() => setLoading(false));
    }, [kind, sp]);

    const counts = data?.counts || {};
    const listings = useMemo(() => sortStoreFirst(data?.listings || [], active?.slug), [data, active?.slug]);
    const mineCount = active?.slug ? listings.filter((l) => (l.store?.slug || l.store_slug) === active.slug).length : 0;
    const accent = active ? cardTheme({ store: active }, active) : MALL_THEME;

    const setKind = (k) => setSp({ kind: k, ...(sp.get("q") ? { q: sp.get("q") } : {}) });
    const search = (e) => { e.preventDefault(); setSp(q ? { q } : { kind: kind || "machine" }); };

    return (
        <Layout>
            <PageBanner room="classifieds" />
            <PageIntro trail={[{ label: "Classifieds", to: "/supplies" }]} kicker="Classifieds · every shop" title="Classifieds" testid="marketplace-intro"
                description="The classifieds page for every print shop on 13thav. Shops post machines, supplies, blanks, apparel, services, fundraisers and printing classes. Every ad says who is selling and where they are. You message the seller from the ad and make the deal yourselves."
                right={active?.slug && (
                    <p className="inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em]"
                        style={{ borderColor: tint(accent.primary, 0.5), background: tint(accent.primary, 0.08), color: accent.ink || accent.primary }}
                        data-testid="marketplace-active-note">
                        {mineCount ? `${active.name}'s ${mineCount === 1 ? "ad shows" : `${mineCount} ads show`} first, in ${active.name}'s colors.` : `${active.name} has no ads on this page yet.`} Other sellers show in black and white.
                    </p>
                )}>
                <Link to={user ? "/supplies/sell" : "/login"} data-testid="sell-cta">
                    <StoreButton theme={accent} data-testid="sell-cta-button"><Plus size={16} /> Post an ad</StoreButton>
                </Link>
                <Link to="/classes" data-testid="classes-cta">
                    <GhostButton><GraduationCap size={16} /> Printing classes</GhostButton>
                </Link>
                <Link to="/services" data-testid="services-cta">
                    <GhostButton><Wrench size={16} /> Install &amp; repair service</GhostButton>
                </Link>
                {user && <Link to="/account/listings"><GhostButton data-testid="my-listings-link">My listings</GhostButton></Link>}
            </PageIntro>

            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap gap-2" data-testid="kind-tabs">
                        {KIND_TABS.map((t) => (
                            <button key={t.key} onClick={() => setKind(t.key)} data-testid={`kind-tab-${t.key}`}
                                className={`rounded-full border px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors ${kind === t.key ? "border-zinc-900 bg-zinc-900 text-white" : "border-white/12 text-zinc-300 hover:border-white/35"}`}>
                                {t.label} <span className="font-mono text-[11px] opacity-70">{counts[t.key] ?? ""}</span>
                            </button>
                        ))}
                    </div>
                    <form onSubmit={search} className="flex gap-2">
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search film, i1600, press…"
                            className="w-full rounded-xl border border-white/15 bg-brand-ink px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:border-zinc-900 focus:outline-none sm:w-72"
                            data-testid="marketplace-search-input" />
                        <GhostButton type="submit" data-testid="marketplace-search-btn"><Search size={15} /></GhostButton>
                    </form>
                </div>

                {!kind && sp.get("q") && (
                    <p className="mt-5 text-sm text-zinc-400" data-testid="search-scope-note">
                        Searching every category for “{sp.get("q")}”.{" "}
                        <button onClick={() => { setQ(""); setSp({ kind: "machine" }); }} className="underline" data-testid="clear-search">Clear search</button>
                    </p>
                )}

                {loading ? (
                    <div className="flex min-h-[40vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div>
                ) : listings.length === 0 ? (
                    <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.03] p-12 text-center" data-testid="marketplace-empty">
                        <p className="text-zinc-400">Nothing here yet. Be the first to list.</p>
                        <Link to={user ? "/supplies/sell" : "/login"} className="mt-4 inline-block"><StoreButton theme={accent}><Plus size={16} /> Post an ad</StoreButton></Link>
                    </div>
                ) : (
                    <div className="mt-7 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="listing-grid">
                        {listings.map((l, i) => (
                            <Reveal key={l.slug} delay={i * 0.04}><ListingCard l={l} /></Reveal>
                        ))}
                    </div>
                )}
            </section>
        </Layout>
    );
}
